/**
 * FERP Portal - Task Reminder Hook (Option A: Browser Notifications)
 *
 * Polls for tasks with upcoming/overdue dueAt and shows browser notifications.
 * ADHD-friendly: multiple reminder windows before due, escalating urgency.
 *
 * Reminder schedule:
 * - 24 hours before: "Tomorrow: {task}"
 * - 1 hour before: "Due soon: {task}"
 * - At due time: "Due now: {task}"
 * - Overdue: "Overdue: {task}" (repeats every 30 min)
 *
 * Uses browser Notification API with permission request on first use.
 */

import { useEffect, useRef, useCallback, useState } from 'react';
import { useQuery } from '@apollo/client/react';
import { gql } from '@apollo/client';
import { useMyActor } from './useMyActor';

const GET_MY_TASKS = gql`
  query GetMyTasksForReminders($actorId: String!) {
    tasksByActor(actorId: $actorId, first: 100) {
      nodes {
        id
        name
        dueAt
        status
      }
    }
  }
`;

interface Task {
  id: string;
  name: string;
  dueAt: string | null;
  status: string;
}

interface ReminderState {
  enabled: boolean;
  permission: NotificationPermission;
  lastCheck: Date | null;
}

// Track which reminders we've already shown to avoid spam
const shownReminders = new Set<string>();

export function useTaskReminders() {
  const { actorId } = useMyActor();
  const [state, setState] = useState<ReminderState>({
    enabled: false,
    permission: 'default',
    lastCheck: null,
  });
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const { data, refetch } = useQuery<{ tasksByActor: { nodes: Task[] } }>(GET_MY_TASKS, {
    variables: { actorId },
    skip: !actorId,
    pollInterval: 60000, // Check every minute
  });

  // Request notification permission
  const requestPermission = useCallback(async () => {
    if (!('Notification' in window)) {
      console.warn('Browser does not support notifications');
      return;
    }

    const permission = await Notification.requestPermission();
    setState((prev) => ({
      ...prev,
      permission,
      enabled: permission === 'granted',
    }));
  }, []);

  // Show a notification
  const showNotification = useCallback((title: string, body: string, taskId: string, urgency: 'low' | 'medium' | 'high') => {
    const reminderKey = `${taskId}-${urgency}-${new Date().toDateString()}`;

    // Don't show same reminder twice in same day
    if (shownReminders.has(reminderKey)) return;
    shownReminders.add(reminderKey);

    if (state.permission !== 'granted') return;

    const icon = urgency === 'high' ? '🚨' : urgency === 'medium' ? '⏰' : '📋';

    new Notification(`${icon} ${title}`, {
      body,
      icon: '/ferp-icon.png',
      tag: taskId, // Replaces previous notification for same task
      requireInteraction: urgency === 'high', // Stay until dismissed for urgent
    });
  }, [state.permission]);

  // Check tasks and trigger reminders
  const checkReminders = useCallback(() => {
    if (!data?.tasksByActor?.nodes) return;

    const now = new Date();
    const tasks = data.tasksByActor.nodes.filter(
      (t) => t.dueAt && t.status !== 'completed' && t.status !== 'cancelled'
    );

    for (const task of tasks) {
      const dueDate = new Date(task.dueAt!);
      const diffMs = dueDate.getTime() - now.getTime();
      const diffHours = diffMs / (1000 * 60 * 60);
      const diffMins = diffMs / (1000 * 60);

      if (diffMs < 0) {
        // Overdue
        const overdueHours = Math.abs(diffHours);
        showNotification(
          'Overdue Task',
          `"${task.name}" was due ${overdueHours < 1 ? `${Math.round(Math.abs(diffMins))} minutes` : `${Math.round(overdueHours)} hours`} ago`,
          task.id,
          'high'
        );
      } else if (diffMins <= 5) {
        // Due in next 5 minutes
        showNotification(
          'Due Now',
          `"${task.name}" is due now!`,
          task.id,
          'high'
        );
      } else if (diffHours <= 1) {
        // Due within 1 hour
        showNotification(
          'Due Soon',
          `"${task.name}" is due in ${Math.round(diffMins)} minutes`,
          task.id,
          'medium'
        );
      } else if (diffHours <= 24 && diffHours > 23) {
        // Due tomorrow (23-24 hour window)
        showNotification(
          'Due Tomorrow',
          `"${task.name}" is due tomorrow`,
          task.id,
          'low'
        );
      }
    }

    setState((prev) => ({ ...prev, lastCheck: now }));
  }, [data, showNotification]);

  // Initialize and start polling
  useEffect(() => {
    if ('Notification' in window) {
      setState((prev) => ({
        ...prev,
        permission: Notification.permission,
        enabled: Notification.permission === 'granted',
      }));
    }
  }, []);

  // Check reminders when data changes
  useEffect(() => {
    if (state.enabled && data) {
      checkReminders();
    }
  }, [state.enabled, data, checkReminders]);

  // Also check every 5 minutes for overdue escalation
  useEffect(() => {
    if (!state.enabled) return;

    intervalRef.current = setInterval(() => {
      refetch();
      checkReminders();
    }, 5 * 60 * 1000); // 5 minutes

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [state.enabled, refetch, checkReminders]);

  return {
    ...state,
    requestPermission,
    checkReminders,
  };
}

/**
 * Clear reminder history (e.g., on logout or new day)
 */
export function clearReminderHistory() {
  shownReminders.clear();
}
